"use client";

import { organizationNameSchema } from "@repo/api/lib/validation";
import { authClient } from "@repo/auth/client";
import { config } from "@repo/config";
import { visibleFieldError } from "@saas/auth/components/AuthNotice";
import {
	organizationsQueryKeys,
	useCreateOrganizationMutation,
} from "@saas/organizations/lib/api";
import { AuthHeading } from "@saas/shared/components/AuthWrapper";
import { useRouter } from "@shared/hooks/router";
import { useForm, useStore } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@ui/components/button";
import { Field, FieldError, FieldLabel } from "@ui/components/field";
import { Input } from "@ui/components/input";
import { Spinner } from "@ui/components/spinner";
import { toast } from "@ui/components/toast";
import { UsersRoundIcon } from "lucide-react";
import { useState } from "react";
import { useActiveOrganization } from "../hooks/use-active-organization";
import { ROLE_COPY, useAnswerInvitation } from "./OrganizationInvitationModal";
import { OrganizationLogo } from "./OrganizationLogo";

/**
 * Shown to a signed-in person who belongs to no retailer. When users may not
 * create organizations (the Hephon Commerce default) it explains how to get
 * access instead of offering a form.
 */
export function CreateOrganizationForm({
	defaultName,
	signedInAs,
	invitations = [],
}: {
	defaultName?: string;
	signedInAs?: string;
	invitations?: PendingInvitation[];
}) {
	if (!config.organizations.enableUsersToCreateOrganizations) {
		return invitations.length > 0 ? (
			<OpenInvitations
				invitations={invitations}
				signedInAs={signedInAs}
			/>
		) : (
			<NoTeamYet signedInAs={signedInAs} />
		);
	}
	return <CreateForm defaultName={defaultName} />;
}

export interface PendingInvitation {
	id: string;
	role: string;
	organizationName: string;
	organizationSlug: string;
	logoUrl?: string | null;
}

/** Someone who signed in without their invitation link picks it up here. */
function OpenInvitations({
	invitations,
	signedInAs,
}: {
	invitations: PendingInvitation[];
	signedInAs?: string;
}) {
	const { pending, answer } = useAnswerInvitation();
	const [declining, setDeclining] = useState<string | null>(null);

	return (
		<div className="flex flex-col">
			<AuthHeading
				title={
					invitations.length === 1
						? "You have an invitation"
						: "You have invitations"
				}
				description={
					signedInAs ? (
						<>
							Signed in as{" "}
							<span className="font-medium text-foreground">
								{signedInAs}
							</span>
							.
						</>
					) : undefined
				}
			/>
			<ul className="flex flex-col gap-3">
				{invitations.map((invitation) => {
					const busy = pending?.invitationId === invitation.id;
					return (
						<li
							key={invitation.id}
							className="flex flex-col gap-4 rounded-3xl bg-card p-4 ring-1 ring-foreground/6 dark:ring-foreground/10"
						>
							<div className="flex min-w-0 items-center gap-3">
								<OrganizationLogo
									name={invitation.organizationName}
									logoUrl={invitation.logoUrl}
									className="size-10"
								/>
								<div className="flex min-w-0 flex-col">
									<span className="truncate font-medium text-sm">
										{invitation.organizationName}
									</span>
									<span className="text-muted-foreground text-xs">
										{ROLE_COPY[invitation.role] ??
											"You'll help run their shopping assistant."}
									</span>
								</div>
							</div>
							{declining === invitation.id ? (
								<div className="flex flex-wrap items-center justify-end gap-2">
									<span className="mr-auto text-muted-foreground text-sm">
										Decline this invitation?
									</span>
									<Button
										variant="ghost"
										onClick={() => setDeclining(null)}
										disabled={pending !== null}
									>
										Keep it
									</Button>
									<Button
										variant="destructive"
										onClick={() =>
											answer(
												invitation.id,
												invitation.organizationSlug,
												false,
											)
										}
										disabled={pending !== null}
									>
										{busy && !pending?.accept ? (
											<Spinner data-icon="inline-start" />
										) : null}
										Decline invitation
									</Button>
								</div>
							) : (
								<div className="flex flex-wrap justify-end gap-2">
									<Button
										variant="ghost"
										onClick={() =>
											setDeclining(invitation.id)
										}
										disabled={pending !== null}
									>
										Decline
									</Button>
									<Button
										onClick={() =>
											answer(
												invitation.id,
												invitation.organizationSlug,
												true,
											)
										}
										disabled={pending !== null}
									>
										{busy && pending?.accept ? (
											<Spinner data-icon="inline-start" />
										) : null}
										Accept and open
									</Button>
								</div>
							)}
						</li>
					);
				})}
			</ul>
		</div>
	);
}

function NoTeamYet({ signedInAs }: { signedInAs?: string }) {
	const [signingOut, setSigningOut] = useState(false);
	return (
		<div className="flex flex-col">
			<span className="mb-6 grid size-11 place-items-center rounded-2xl bg-muted text-muted-foreground [&_svg]:size-5">
				<UsersRoundIcon />
			</span>
			<AuthHeading
				title="You're not on a team yet"
				description={
					<>
						{signedInAs ? (
							<>
								<span className="font-medium text-foreground">
									{signedInAs}
								</span>{" "}
								isn't on any team.{" "}
							</>
						) : null}
						Ask your admin for an invitation.
					</>
				}
			/>
			<Button
				variant="outline"
				size="lg"
				disabled={signingOut}
				onClick={() => {
					setSigningOut(true);
					authClient.signOut({
						fetchOptions: {
							onSuccess: () => {
								window.location.href =
									config.auth.redirectAfterLogout;
							},
						},
					});
				}}
			>
				{signingOut ? <Spinner data-icon="inline-start" /> : null}
				Sign in with a different email
			</Button>
		</div>
	);
}

function CreateForm({ defaultName }: { defaultName?: string }) {
	const router = useRouter();
	const queryClient = useQueryClient();
	const { setActiveOrganization } = useActiveOrganization();
	const createOrganization = useCreateOrganizationMutation();

	const form = useForm({
		defaultValues: { name: defaultName ?? "" },
		onSubmit: async ({ value }) => {
			try {
				const organization = await createOrganization.mutateAsync({
					name: value.name,
				});
				if (!organization) {
					throw new Error("No organization returned");
				}
				await setActiveOrganization(organization.slug);
				await queryClient.invalidateQueries({
					queryKey: organizationsQueryKeys.list(),
				});
				router.replace(`/app/${organization.slug}`);
			} catch {
				toast.add({
					type: "error",
					title: "Couldn't create the workspace",
					description: "Try again in a moment.",
				});
			}
		},
	});

	const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
	const attempted = useStore(
		form.store,
		(state) => state.submissionAttempts > 0,
	);

	return (
		<div className="flex flex-col">
			<AuthHeading
				title="Name your workspace"
				description="You can rename it later."
			/>
			<form
				className="flex flex-col gap-5"
				noValidate
				onSubmit={(event) => {
					event.preventDefault();
					event.stopPropagation();
					form.handleSubmit();
				}}
			>
				<form.Field
					name="name"
					validators={{ onBlur: organizationNameSchema }}
				>
					{(field) => {
						const error = visibleFieldError(field, attempted);
						const invalid = !!error;
						return (
							<Field data-invalid={invalid || undefined}>
								<FieldLabel htmlFor="name">
									Workspace name
								</FieldLabel>
								<Input
									id="name"
									value={field.state.value}
									onChange={(event) =>
										field.handleChange(event.target.value)
									}
									onBlur={field.handleBlur}
									autoComplete="organization"
									autoFocus
									aria-invalid={invalid || undefined}
								/>
								{invalid ? <FieldError errors={error} /> : null}
							</Field>
						);
					}}
				</form.Field>
				<Button type="submit" size="lg" disabled={isSubmitting}>
					{isSubmitting ? <Spinner data-icon="inline-start" /> : null}
					Create workspace
				</Button>
			</form>
		</div>
	);
}
